
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../redux/store';
import { selectConfig } from '../../redux/slices/configSlice';
import { selectPlayers } from '../../redux/slices/playersSlice';
import { signIn } from '../../redux/slices/userSlice';
import { setPage } from '../../redux/slices/pageSlice';
import { getUrlParam, capitalizeWords } from '../../utils/common';
import Chat from '../../containers/Chat/chat';

// Define types for the state and props
interface ErrorState {
    username?: string;
    email?: string;
    inquiry?: string;
    lastName?: string;
    deposit?: string;
    product1?: string;
}

interface InquiryOption {
    Label: string;
    value: string;
    display: boolean;
}

interface ProductOption {
    Brandname: string;
    Value: string;
}
interface User {
    brand: string;
    level: string;
    lang: string;
    country: string;
    product: string;
    inquiry: string;
    source: string;
    payment: string;
    opm: string;
    init_msg?: string;
    referer: string;
    refererPlatform: string;
    cat: string;
}

interface AutoSignInProps {
  mParam?: string | null;
  mUsername?: string | null;
  mEmail?: string | null;
  mReferer?: string | null;
}

const AutoSignIn: React.FC<AutoSignInProps> = ({ 
  mParam, 
  mUsername, 
  mEmail, 
  mReferer 
}) => {
    const dispatch = useAppDispatch();

    const config = useSelector(selectConfig);
    const formData = config?.data?.[0]?.attributes;


    const players = useSelector(selectPlayers);
    const playerData = players?.data ? players?.data.attributes : null;

    let lang: string = "EN";
    let locale = "";

    const langMap: Record<string, string> = {
    "en-IN": "in",
    "kk": "kz",
    "zh-Hans": "cn",
    "ko-KR": "kr",
    "vi": "vn",
    "ne": "np",
    "zh-Hant": "cn",
    "bn-BD": "bd",
    "en-PK": "pk",
    // Add more mappings as needed
    };
    
    if (Array.isArray(config.data) && config.data.length > 0) {
        locale = config.data[0].attributes.locale;
        console.log(config.data[0].attributes.locale, "jHGJGJGFJGFKWGFKFJKFJBK");
    } else {
        console.error("config.data is not an array or is empty");
    }

    lang = locale ?? lang;
    lang = langMap[ locale] ?? lang;
    lang = lang.toUpperCase();

    const generateUserLevel = function() {
        let level = 'REG';
        let levelInt = getUrlParam('level');
    
        if(levelInt) {
            if(["17", "26"].includes(levelInt)) {
            level = "VIP"
            } else if(["13", "18"].includes(levelInt)) {
            level = "Elite"
            } else {
            level = "REG"
            }
        }
        return level;
    }

    const appGenerateSkillset = (user: User): string => {

        let cat = user.cat;
        cat = capitalizeWords(cat)
        if(cat == "Dafabet") {
            cat = "Dafa";
        }

        let options = [
                    'WC',
                    lang,
                    cat,
                    '__REG_PI'
                ];

        return options.join('_');
    }

    useEffect(() => {
        if (!formData) return;
      
        const resolvedLang = locale === "zh-Hans" ? "CN" : lang ?? "EN";
      
        const getBrand = () => {
          const templateBrand = formData?.Template?.Brand ?? "";
          const brandMapping: Record<string, string> = {
            dfbt: "dafabet",
            sevenbaby: "777baby",
          };
          let brand = brandMapping[templateBrand] ?? templateBrand ?? "dafabet";
          if (window.location.pathname.split("/").pop() === "dafa-spain") {
            brand = "dafabetes";
          }
          if (
            window.location.hostname === "m-istg-chat.elysium-dfbt.com" ||
            window.location.hostname === "localhost"
          ) {
            lang = `ISTG ${resolvedLang}`;
            brand = `istg-${brand}`;
          }
          return brand;
        };
      
        const user: User = {
          brand: getUrlParam("brand") ?? "Dafa",
          level: generateUserLevel(),
          lang: resolvedLang,
          country: config.countryCode ?? "",
          product: getUrlParam("product") ?? "",
          inquiry: getUrlParam("inquiry") ?? "",
          source: config.playtech ? "Playtech" : "EWC",
          payment: getUrlParam("payment") ?? "",
          opm: getUrlParam("otherpayment") ?? "NULL",
          init_msg: config.init_msg,
          referer: mReferer ?? config.source ?? "",
          refererPlatform: navigator.userAgent,
          cat: getBrand(),
        };
      
        if (user.cat === "zedbet" && ["EN", "KR"].includes(resolvedLang)) {
          user.inquiry = "GI";
        }
      
        const product = user.product.toLowerCase() === "dafabet" ? "Dafabet" : user.product;
      
        const skillset = appGenerateSkillset(user);
      
        // Use mUsername and mEmail if available, otherwise fall back to playerData or defaults
        const username = mUsername ?? playerData?.username ?? "";
        const email = mEmail ?? playerData?.email ?? "default@dafabet.com";
      
        const customValues = [
          { title: "IP", value: formData?.PreChatSurvey.clientIp ?? "202.151.35.161" },
          { title: "country", value: formData?.PreChatSurvey.countryCode ?? "" },
          { title: "referer", value: mReferer ?? formData?.source ?? "" },
          { title: "RefererPlatform", value: navigator.userAgent },
          { title: "CustomerVerified", value: playerData || mUsername || mEmail ? "true" : "false" },
          { title: "language", value: resolvedLang.toUpperCase() },
          { title: "CustomerSegment", value: user.level },
          { title: "SuggestedPM", value: user.payment },
          { title: "OtherPM", value: user.opm },
          { title: "Restart", value: "false" },
          { title: "skillset", value: skillset },
          { title: "username", value: username },
          { title: "name", value: username },
          { title: "email", value: email },
          { title: "phoneNumber", value: "" },
          { title: "product", value: product },
          { title: "inquiry", value: user.inquiry },
          { title: "subinquiry", value: "" },
          { title: "deposit", value: "" },
          { title: "Product", value: product },
          { title: "Brand", value: user.cat },
        ];

        const engagementParameters = Object.fromEntries(
          customValues.map(({ title, value }) => [title, value])
        );
      
        const userData = {
          customerIdentifier: email,
          customerIdentifiers: {
            emailAddresses: [email],
            username: [username || "default"],
          },
          displayName: username || "default",
          firstName: username || "default",
          emailAddress: email,
          engagementParamters: engagementParameters,
        };
      
        dispatch(signIn({ signInData: userData, engagementParams: engagementParameters })).then(
          (result) => {
            if (result.meta.requestStatus === "fulfilled") {
              dispatch(setPage("chat"));
            }
          }
        );
      }, [formData]);
      

    return (
        <div>
            {<Chat mParam={mParam} />}
        </div>
    );
};

export default AutoSignIn;

